// Advanced Analytics & A/B Testing Suite

// Event tracking (no-op; GA removed)
export const trackEvent = (eventName, parameters = {}) => {
  if (process.env.NODE_ENV === 'development') {
    console.log('📊 Event:', eventName, parameters);
  }
};

// Form analytics tracking
export class FormAnalytics {
  constructor(formName) {
    this.formName = formName;
    this.startTime = Date.now();
    this.stepTimes = {};
    this.fieldInteractions = {};
  }

  trackStepStart(stepIndex, stepName) {
    this.stepTimes[stepIndex] = { start: Date.now(), name: stepName };
    trackEvent('form_step_start', {
      form_name: this.formName,
      step_index: stepIndex,
      step_name: stepName
    });
  }

  trackStepComplete(stepIndex) {
    const stepData = this.stepTimes[stepIndex];
    if (stepData) {
      const duration = Date.now() - stepData.start;
      trackEvent('form_step_complete', {
        form_name: this.formName,
        step_index: stepIndex,
        step_name: stepData.name,
        duration_ms: duration
      });
    }
  }

  trackFieldInteraction(fieldName, interactionType = 'focus') {
    if (!this.fieldInteractions[fieldName]) {
      this.fieldInteractions[fieldName] = [];
    }
    
    this.fieldInteractions[fieldName].push({
      type: interactionType,
      timestamp: Date.now()
    });

    trackEvent('form_field_interaction', {
      form_name: this.formName,
      field_name: fieldName,
      interaction_type: interactionType
    });
  }

  trackFormAbandon(currentStep) {
    const totalTime = Date.now() - this.startTime;
    trackEvent('form_abandon', {
      form_name: this.formName,
      current_step: currentStep,
      total_time_ms: totalTime,
      field_interactions: Object.keys(this.fieldInteractions).length
    });
  }

  trackFormSubmit() {
    const totalTime = Date.now() - this.startTime;
    trackEvent('form_submit', {
      form_name: this.formName,
      total_time_ms: totalTime,
      total_steps: Object.keys(this.stepTimes).length,
      field_interactions: Object.keys(this.fieldInteractions).length
    });
  }
}

// A/B Testing Framework
export class ABTesting {
  constructor() {
    this.tests = new Map();
    this.userBucket = this.getUserBucket();
  }

  getUserBucket() {
    let bucket = localStorage.getItem('ab_user_bucket');
    if (!bucket) {
      bucket = Math.random().toString(36).substring(7);
      localStorage.setItem('ab_user_bucket', bucket);
    }
    return bucket;
  }

  createTest(testName, variants, trafficSplit = 0.5) {
    const hash = this.hashString(this.userBucket + testName);
    const bucketValue = hash % 100;
    const selectedVariant = bucketValue < (trafficSplit * 100) ? variants[0] : variants[1];
    
    this.tests.set(testName, {
      variant: selectedVariant,
      variants,
      trafficSplit
    });

    // Track A/B test assignment
    trackEvent('ab_test_assignment', {
      test_name: testName,
      variant: selectedVariant,
      user_bucket: this.userBucket
    });

    return selectedVariant;
  }

  getVariant(testName) {
    const test = this.tests.get(testName);
    return test ? test.variant : null;
  }

  trackConversion(testName, conversionType = 'primary') {
    const test = this.tests.get(testName);
    if (test) {
      trackEvent('ab_test_conversion', {
        test_name: testName,
        variant: test.variant,
        conversion_type: conversionType,
        user_bucket: this.userBucket
      });
    }
  }

  hashString(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash);
  }
}

// Conversion tracking
export const trackConversion = (type, value = 0, metadata = {}) => {
  trackEvent('conversion', {
    conversion_type: type,
    conversion_value: value,
    ...metadata
  });

  // Track business-specific conversions
  switch (type) {
    case 'application_start':
      trackEvent('begin_checkout', { currency: 'USD', value: 0 });
      break;
    case 'application_submit':
      trackEvent('purchase', { currency: 'USD', value: 2500 }); // Estimated lead value
      break;
    case 'phone_click':
      trackEvent('contact', { method: 'phone' });
      break;
    case 'email_click':
      trackEvent('contact', { method: 'email' });
      break;
    default:
      // No specific tracking for unknown conversion types
      break;
  }
};

// Heat mapping and user behavior
export const trackUserBehavior = (action, element, metadata = {}) => {
  trackEvent('user_behavior', {
    action,
    element,
    page: window.location.pathname,
    timestamp: Date.now(),
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    ...metadata
  });
};

// Page performance tracking
export const trackPagePerformance = () => {
  if (!('performance' in window)) return;

  const perfData = performance.getEntriesByType('navigation')[0];
  if (!perfData) return;

  // Navigation Timing Level 2: values are already ms since navigation start
  const round = (n) => (typeof n === 'number' && Number.isFinite(n) ? Math.round(n) : null);
  const paintEntries = performance.getEntriesByType('paint') || [];
  const firstPaint = paintEntries.find((e) => e.name === 'first-paint' || e.name === 'first-contentful-paint');

  const pageLoadTime = round(perfData.duration || perfData.loadEventEnd);
  const domContentLoaded = round(perfData.domContentLoadedEventEnd);
  const firstPaintMs = round(firstPaint?.startTime ?? perfData.responseStart);

  if (pageLoadTime == null && domContentLoaded == null && firstPaintMs == null) return;

  trackEvent('page_performance', {
    page_load_time: pageLoadTime,
    dom_content_loaded: domContentLoaded,
    first_paint: firstPaintMs,
    page_url: window.location.pathname,
  });
};

// Initialize analytics on page load
export const initializeAnalytics = () => {
  // Track page view
  trackEvent('page_view', {
    page_title: document.title,
    page_location: window.location.href,
    page_path: window.location.pathname
  });

  // Track performance after load
  window.addEventListener('load', () => {
    setTimeout(trackPagePerformance, 100);
  });

  // Track user engagement time
  let startTime = Date.now();
  let isActive = true;

  const trackEngagement = () => {
    if (isActive) {
      const engagementTime = Date.now() - startTime;
      if (engagementTime > 10000) { // Only track if user was engaged for 10+ seconds
        trackEvent('user_engagement', {
          engagement_time_msec: engagementTime,
          page_path: window.location.pathname
        });
      }
    }
  };

  // Track when user becomes inactive
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      isActive = false;
      trackEngagement();
    } else {
      isActive = true;
      startTime = Date.now();
    }
  });

  // Track before page unload
  window.addEventListener('beforeunload', trackEngagement);
};

// Custom hooks for React components
export const useAnalytics = (componentName) => {
  const formAnalytics = new FormAnalytics(componentName);
  const abTesting = new ABTesting();

  return {
    trackEvent: (eventName, params) => trackEvent(eventName, { component: componentName, ...params }),
    trackConversion,
    trackUserBehavior: (action, element, metadata) => 
      trackUserBehavior(action, element, { component: componentName, ...metadata }),
    formAnalytics,
    abTesting
  };
}; 